// The web's real timings for Grafana (Analytics::WebPerf, `web_perf`): the Core Web Vitals, the first
// map's render, each route compute. Queued in memory and sent in ONE beacon when the page is hidden;
// web-vitals is fetched after `load`, so nothing here weighs on the first screen.
const ENDPOINT = '/api/v1/tracking/perf';
// Analytics::WebPerf::MAX_SAMPLES: the server keeps no more per beacon.
const MAX_SAMPLES = 20;
// Three beacons at most at hide, or the browser's beacon quota refuses the last (web-vitals' final INP/CLS).
const MAX_QUEUE = MAX_SAMPLES * 3;

// Analytics::WebPerf::DETAIL_LENGTH: free text the server keeps, cut to this length.
const DETAIL_LENGTH = 120;
// INP subparts, named as the server's `phase` tag.
const INP_PHASES = {input_delay: 'inputDelay', processing: 'processingDuration', presentation: 'presentationDelay'};

let queue = [];
let listening = false;
let mapReadyRecorded = false;

const slowestInpPhase = (attribution) => Object.keys(INP_PHASES)
    .reduce((slowest, phase) => ((attribution[INP_PHASES[phase]] || 0) > (attribution[INP_PHASES[slowest]] || 0) ? phase : slowest));

// The script file only: a query string or hash would split one script into many rows.
const longestScriptSource = (longestScript) => {
    const source = longestScript?.entry?.sourceURL || longestScript?.entry?.invoker;
    return source ? source.split(/[?#]/)[0] : undefined;
};

const flush = () => {
    if (document.visibilityState !== 'hidden' || !queue.length) {
        return;
    }

    const samples = queue;
    queue = [];

    if (typeof navigator.sendBeacon !== 'function') {
        return;
    }

    for (let i = 0; i < samples.length; i += MAX_SAMPLES) {
        const body = JSON.stringify({page: window.perfPage || '', samples: samples.slice(i, i + MAX_SAMPLES)});
        navigator.sendBeacon(ENDPOINT, new Blob([body], {type: 'application/json'}));
    }
};

// On window, bubbling: web-vitals reports its last LCP/CLS/INP from window CAPTURE listeners on the
// same event, so they are queued before this sends.
const listen = () => {
    if (listening) {
        return;
    }

    listening = true;
    window.addEventListener('visibilitychange', flush);
};

const WebPerf = {
    // metric: one of Analytics::WebPerf::METRICS; tags: Analytics::WebPerf::TAGS (stringified, the server filters them);
    // details: Analytics::WebPerf::DETAILS, free text kept as fields.
    record(metric, value, tags = {}, details = {}) {
        if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
            return;
        }

        const sample = {metric, value: Math.round(value * 1000) / 1000};
        Object.entries(tags).forEach(([key, tag]) => {
            if (tag !== undefined && tag !== null) {
                sample[key] = String(tag);
            }
        });
        Object.entries(details).forEach(([key, detail]) => {
            if (detail) {
                sample[key] = String(detail).slice(0, DETAIL_LENGTH);
            }
        });

        queue.push(sample);
        if (queue.length > MAX_QUEUE) {
            queue.shift();
        }
        listen();
    },

    // The first map is timed from the navigation (what the visitor waited for), a later one from its creation.
    recordMapReady(createdAt) {
        const first = !mapReadyRecorded;
        mapReadyRecorded = true;
        WebPerf.record('map_ready', first ? performance.now() : performance.now() - createdAt, {first});
    },

    initialize() {
        const start = () => {
            import(/* webpackChunkName: "web-vitals" */ 'web-vitals/attribution')
                .then(({onLCP, onINP, onCLS, onFCP, onTTFB}) => {
                    const report = ({name, value, rating, attribution}) => {
                        if (name !== 'INP' || !attribution) {
                            WebPerf.record(name.toLowerCase(), value, {rating});
                            return;
                        }

                        WebPerf.record('inp', value,
                            {rating, interaction: attribution.interactionType, phase: slowestInpPhase(attribution)},
                            {target: attribution.interactionTarget, script: longestScriptSource(attribution.longestScript)});
                    };
                    [onLCP, onINP, onCLS, onFCP, onTTFB].forEach((observe) => observe(report));
                })
                .catch(() => null);
        };

        if (document.readyState === 'complete') {
            start();
        } else {
            window.addEventListener('load', start, {once: true});
        }
    }
};

export default WebPerf;
